import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AxiosInstance } from 'axios'
import { useApplicationContext } from '@mysaiva/core/application'
import type { DocResource } from '../types'
import openapiSnapshot from './openapiSnapshot.json'
import type { OpenApiSpec } from './openapiSchema.types'
import { fetchExternalOpenApiSpec, isOpenApiSpec } from './apiDocsClient'
import { resourcesFromOpenApi } from './fromOpenApi'
import { overviewFromOpenApi } from './overviewFromOpenApi'
import type { OverviewSection } from './overviewFromOpenApi'

export const openApiSpecQueryKey = (orgId: string) =>
  ['developer-platform', 'openapi-spec', orgId] as const

export type OpenApiSpecSource = 'live' | 'snapshot'

export interface OpenApiSpecResult {
  spec: OpenApiSpec
  source: OpenApiSpecSource
}

/**
 * Local override: an arbitrary (public) OpenAPI URL fetched without
 * credentials, for working against a backend that does not serve the
 * org-scoped `api-docs` route yet. Unset in every deployed environment.
 */
function overrideOpenApiUrl(env: ImportMetaEnv = import.meta.env): string | undefined {
  return env.VITE_DEVELOPER_PLATFORM_OPENAPI_URL || undefined
}

async function fetchOverrideSpec(url: string): Promise<OpenApiSpec> {
  const response = await fetch(url)
  if (!response.ok) throw new Error(`OpenAPI fetch failed: ${response.status}`)
  const body: unknown = await response.json()
  if (!isOpenApiSpec(body)) throw new Error('Override URL did not return an OpenAPI document.')
  return body
}

/**
 * Loads the organisation's published external API document
 * (`integrations/v1/orgs/{org_id}/api-docs/openapi.json`) through the
 * authenticated client; falls back to the bundled snapshot
 * (`openapiSnapshot.json`) on any failure — missing route on older backends,
 * missing permission, network error — so the docs pages never break.
 * `source` tells the page which one was actually used.
 */
export async function fetchOpenApiSpec(
  apiClient: AxiosInstance,
  orgId: string,
  overrideUrl: string | undefined = overrideOpenApiUrl()
): Promise<OpenApiSpecResult> {
  try {
    const spec = overrideUrl
      ? await fetchOverrideSpec(overrideUrl)
      : await fetchExternalOpenApiSpec(apiClient, orgId)
    return { spec, source: 'live' }
  } catch {
    return { spec: openapiSnapshot as OpenApiSpec, source: 'snapshot' }
  }
}

/** The organisation's OpenAPI document (live, or the snapshot fallback); one cached query. */
function useOpenApiSpec() {
  const { apiClient, orgContext } = useApplicationContext()
  const orgId = orgContext.org.id

  return useQuery({
    queryKey: openApiSpecQueryKey(orgId),
    queryFn: () => fetchOpenApiSpec(apiClient, orgId),
    staleTime: 5 * 60_000,
    retry: false
  })
}

export function useOpenApiResources(): {
  resources: DocResource[]
  source: OpenApiSpecSource | undefined
  isPending: boolean
  refetch: () => void
} {
  const { data, isPending, refetch } = useOpenApiSpec()
  const resources = useMemo(() => (data ? resourcesFromOpenApi(data.spec) : []), [data])
  return { resources, source: data?.source, isPending, refetch: () => void refetch() }
}

/**
 * The API overview the backend publishes with the document (`info.description`:
 * authentication, standard responses, error codes, pagination, rate limiting, …).
 */
export function useOpenApiOverview(): {
  sections: OverviewSection[]
  source: OpenApiSpecSource | undefined
  isPending: boolean
  refetch: () => void
} {
  const { data, isPending, refetch } = useOpenApiSpec()
  const sections = useMemo(() => (data ? overviewFromOpenApi(data.spec) : []), [data])
  return { sections, source: data?.source, isPending, refetch: () => void refetch() }
}
